import { queryOptions } from "@tanstack/react-query";
import {
  loadBillingOverview,
  loadFactsPlane,
  loadWebStudioBootSnapshot,
  type WebStudioBootSnapshot,
  type WebStudioScope,
} from "../api/boot";
import type { WebStudioCloudApiClient, WebStudioFactsPlaneSnapshot } from "../api/generatedClient";
import { assertNoDurableBearerTokenStorage, reconcileBrowserState } from "../state/browserState";

export interface BootReadResult {
  boot: WebStudioBootSnapshot;
  factsPlane: WebStudioFactsPlaneSnapshot;
  protectedStateCleared: boolean;
}

/**
 * Query keys carry cache identity only: the raw scope selection plus an
 * explicit client-side refresh generation. All semantics — authorization,
 * compatibility, ordering, counts, provenance — stay inside the generated
 * boot snapshot the query returns.
 */
export function bootReadQueryOptions(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  refreshToken: number,
) {
  return queryOptions({
    queryKey: ["webStudioBootRead", refreshToken, scope] as const,
    queryFn: async ({ signal }): Promise<BootReadResult> => {
      assertNoDurableBearerTokenStorage();
      const boot = await loadWebStudioBootSnapshot(client, scope, { signal });
      const factsPlane = await loadFactsPlane(client, scope, { signal, bootSnapshot: boot });
      const { protectedStateCleared } = reconcileBrowserState(
        scope,
        `${boot.responseIdentity.generatedScopeDigest}:${boot.responseIdentity.routeStateDigest}`,
      );
      return { boot, factsPlane, protectedStateCleared };
    },
  });
}

export function billingOverviewQueryOptions(
  client: WebStudioCloudApiClient,
  scope: WebStudioScope,
  refreshToken: number,
) {
  return queryOptions({
    queryKey: ["webStudioBillingOverview", refreshToken, scope] as const,
    queryFn: () => loadBillingOverview(client, scope),
  });
}
